"use client"

import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { ChevronDown, ChevronUp, Calendar, Pickaxe, Mountain, Gem, FileText, Clock } from "lucide-react"

export function PastReports({ pastReports, formatDate, onViewDetails }) {
  const [expandedReports, setExpandedReports] = useState(false)

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  }

  return (
    <motion.section initial="hidden" animate="visible" variants={containerVariants} className="mb-12 max-w-full">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="bg-indigo-100 p-2 rounded-full">
            <Clock className="h-5 w-5 text-indigo-600" />
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-800">Past Reports</h2>
        </div>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setExpandedReports(!expandedReports)}
          className={`flex items-center gap-1 px-3 md:px-4 py-1.5 md:py-2 rounded-full text-sm font-medium transition-colors ${
            expandedReports
              ? "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          {expandedReports ? (
            <>
              <ChevronUp className="h-4 w-4" />
              <span className="hidden sm:inline">Show Less</span>
              <span className="sm:hidden">Less</span>
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4" />
              <span className="hidden sm:inline">Show All</span>
              <span className="sm:hidden">More</span>
            </>
          )}
        </motion.button>
      </div>

      <motion.div
        className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden max-w-full"
        variants={itemVariants}
      >
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 overflow-x-auto">
          <div className="grid grid-cols-3 md:grid-cols-5 p-3 md:p-4 font-medium text-gray-600 min-w-[300px] md:min-w-[600px]">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-500 flex-shrink-0" />
              <span className="truncate">Date</span>
            </div>
            <div className="hidden md:flex items-center gap-2">
              <Pickaxe className="h-4 w-4 text-blue-500 flex-shrink-0" />
              <span className="truncate">Caving</span>
            </div>
            <div className="hidden md:flex items-center gap-2">
              <Mountain className="h-4 w-4 text-gray-500 flex-shrink-0" />
              <span className="truncate">Useless Stones</span>
            </div>
            <div className="flex items-center gap-2">
              <Gem className="h-4 w-4 text-emerald-500 flex-shrink-0" />
              <span className="truncate">Emeralds</span>
            </div>
            <div className="text-right">
              <FileText className="h-4 w-4 text-gray-500 inline flex-shrink-0" />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          {pastReports.length === 0 ? (
            <motion.div variants={itemVariants} className="p-8 text-center">
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Calendar className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-500 font-medium mb-1">No reports found</p>
              <p className="text-gray-400 text-sm mb-6">Past mining reports will appear here</p>
            </motion.div>
          ) : (
            <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
              <AnimatePresence>
                {pastReports.slice(0, expandedReports ? pastReports.length : 2).map((report, index) => (
                  <motion.div
                    key={report.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                    className="grid grid-cols-3 md:grid-cols-5 p-3 md:p-4 hover:bg-gray-50 transition-colors min-w-[300px] md:min-w-[600px]"
                  >
                    <div className="flex items-center text-gray-700 font-medium truncate text-sm md:text-base">
                      {formatDate(report.date)}
                    </div>

                    <div className="hidden md:flex items-center">
                      <div className="bg-blue-50 text-blue-700 font-medium px-2 md:px-3 py-1 rounded-full border border-blue-100 truncate text-sm">
                        {report.caving}m
                      </div>
                    </div>

                    <div className="hidden md:flex items-center">
                      <div className="bg-gray-50 text-gray-700 font-medium px-2 md:px-3 py-1 rounded-full border border-gray-100 truncate text-sm">
                        {report.uselessStones}
                      </div>
                    </div>

                    <div className="flex items-center">
                      <div className="bg-emerald-50 text-emerald-700 font-medium px-2 md:px-3 py-1 rounded-full border border-emerald-100 truncate text-sm">
                        {report.emeraldsFound}
                      </div>
                    </div>

                    <div className="text-right">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="px-3 py-1.5 text-xs md:text-sm bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-full border border-indigo-100 transition-colors font-medium whitespace-nowrap"
                        onClick={() => onViewDetails(report)}
                      >
                        <span className="hidden sm:inline">View Details</span>
                        <span className="sm:hidden">View</span>
                      </motion.button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>

        {pastReports.length > 0 && !expandedReports && pastReports.length > 2 && (
          <motion.div
            variants={itemVariants}
            className="bg-gradient-to-b from-transparent to-gray-50 p-4 text-center border-t border-gray-100"
          >
            <p className="text-gray-500 text-sm">
              Showing {Math.min(2, pastReports.length)} of {pastReports.length} reports
            </p>
          </motion.div>
        )}
      </motion.div>
    </motion.section>
  )
}
